import React from "react";
import { Chrome } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Hero() {
    const { t } = useTranslation();
    return (
        <section
            className="relative border-b-2 border-black bg-white"
            data-testid="hero-section"
        >
            {/* Top ribbon */}
            <div className="border-b-2 border-black bg-[#FFCC00] h-2" aria-hidden="true" role="presentation" />

            <div className="mx-auto max-w-[1920px] flex flex-col lg:flex-row">
                {/* Left column - fills remaining space, centers content */}
                <div className="flex-1 min-w-0 border-b-2 lg:border-b-0 lg:border-r-2 border-black p-8 md:p-12 lg:p-16">
                    <div className="flex items-center gap-3 mb-8">
                        <span className="font-mono text-xs tracking-[0.25em] uppercase border-2 border-black bg-white px-3 py-1">
                            {t("landing.hero.eyebrow")}
                        </span>
                    </div>

                    <h1
                        className="font-display text-5xl md:text-7xl lg:text-[5.5rem] uppercase leading-[0.9] tracking-tighter"
                        data-testid="hero-headline"
                    >
                        {t("landing.hero.headlineLine1")}
                        <br />
                        {t("landing.hero.headlineLine2")}
                        <br />
                        <span className="inline-block bg-[#FFCC00] text-black px-3 -mx-1">
                            {t("landing.hero.headlineHighlight")}
                        </span>
                    </h1>

                    <p
                        className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-zinc-700"
                        data-testid="hero-subheadline"
                    >
                        {t("landing.hero.subheadline")}
                    </p>

                    <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600">
                        <span>{t("landing.hero.checkBrowser")}</span>
                        <span>{t("landing.hero.checkCredit")}</span>
                        <span>{t("landing.hero.checkBulk")}</span>
                    </div>

                    {/* CTAs row */}
                    <div className="mt-10 flex flex-col gap-4">
                        <div className="flex flex-wrap items-center gap-4">
                            <a
                                href="/sign-up"
                                data-testid="hero-primary-cta"
                                className="btn-brutal border-2 border-black bg-black text-white px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] shadow-brutal-sm inline-flex items-center gap-2"
                            >
                                <Chrome size={14} /> {t("landing.hero.ctaExtension")}
                            </a>
                            <a
                                href="/sign-up"
                                data-testid="hero-free-credits-cta"
                                className="btn-brutal border-2 border-black bg-white text-black px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] shadow-brutal-sm inline-flex items-center gap-2"
                            >
                                {t("landing.hero.ctaFreeCredits")}
                            </a>
                        </div>
                    </div>
                </div>

                {/* Right column - report mock takes its natural width */}
                <div className="shrink-0 relative grid-bg-dense overflow-visible">
                    <ReportMock />
                </div>
            </div>
        </section>
    );
}

function ReportMock() {
    const { t } = useTranslation();
    return (
        <div
            className="relative h-full p-4 md:p-6 lg:p-8"
            data-testid="hero-report-mock"
        >
            <div className="border-2 border-black bg-white shadow-brutal-lg">
                {/* Header */}
                <div className="flex items-stretch border-b-2 border-black">
                    <div className="flex-1 p-4">
                        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                            {t("landing.hero.mockReport")}
                        </div>
                        <div className="font-display uppercase text-xl mt-1 leading-none">
                            VW Golf 2.0 TDI
                        </div>
                        <div className="font-mono text-xs text-zinc-700 mt-1">
                            2018 · 124,500 km · Diesel
                        </div>
                    </div>
                    <div className="border-l-2 border-black bg-[#34C759] text-black px-4 flex flex-col items-center justify-center">
                        <span className="font-mono text-[9px] uppercase tracking-[0.2em]">
                            {t("landing.hero.mockVerdict")}
                        </span>
                        <span className="font-display text-xl uppercase">
                            {t("landing.hero.mockGood")}
                        </span>
                    </div>
                </div>

                {/* Score */}
                <div className="border-b-2 border-black p-4 bg-black text-white">
                    <div className="font-mono text-[10px] tracking-[0.25em] uppercase opacity-70">
                        {t("landing.hero.mockScore")}
                    </div>
                    <div className="font-display text-5xl mt-1 leading-none">
                        82
                    </div>
                    <div className="font-mono text-[10px] uppercase mt-1 opacity-70">
                        {t("landing.hero.mockOf100")}
                    </div>
                </div>

                {/* Summary */}
                <div className="border-b-2 border-black p-4">
                    <p className="font-mono text-xs text-zinc-700 leading-relaxed">
                        {t("landing.hero.mockSummary")}
                    </p>
                </div>

                {/* Pros / Cons tabbed carousel */}
                <div className="border-b-2 border-black">
                    <div className="flex border-b-2 border-black">
                        <button className="flex-1 border-r-2 border-black px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase bg-zinc-100">
                            {t("landing.hero.mockProCount")}
                        </button>
                        <button className="flex-1 px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400">
                            {t("landing.hero.mockConsCount")}
                        </button>
                    </div>
                    <div className="p-4">
                        <ul className="space-y-1 text-xs leading-snug">
                            <li>{t("landing.hero.mockPro1")}</li>
                            <li>{t("landing.hero.mockPro2")}</li>
                            <li>{t("landing.hero.mockPro3")}</li>
                        </ul>
                    </div>
                </div>

                {/* Price */}
                <div className="p-4 flex items-end justify-between border-b-2 border-black">
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                            {t("landing.hero.mockAsking")}
                        </div>
                        <div className="font-display text-2xl uppercase leading-none mt-1">
                            €14,900
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                            {t("landing.hero.mockVsMarket")}
                        </div>
                        <div className="font-display text-2xl uppercase leading-none mt-1 text-green-700">
                            −6.2%
                        </div>
                    </div>
                </div>

                {/* View Full Report button */}
                <div className="p-4">
                    <div className="border-2 border-black px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-center">
                        {t("landing.hero.mockViewReport")}
                    </div>
                </div>
            </div>

            {/* floating badges */}
            <div className="hidden md:block absolute -bottom-4 -left-2 border-2 border-black bg-[#FFCC00] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] shadow-brutal-sm rotate-[-3deg]">
                {t("landing.hero.mockVinVerified")}
            </div>
            <div className="hidden md:block absolute top-2 right-2 border-2 border-black bg-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] shadow-brutal-sm rotate-[2deg]">
                {t("landing.hero.mockAnalysisTime")}
            </div>
        </div>
    );
}
